import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EndorsementLicenceTypeClient, EndorsementLicenceTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new EndorsementLicenceTypeClient(API_BASE_URL, axiosInstance);

export const useEndorsementLicenceTypes = () =>
  useQuery({
    queryKey: ['endorsementLicenceTypes'],
    queryFn: async () => {
      const response = await getApiClient().searchEndorsementLicenceTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useEndorsementLicenceTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchEndorsementLicenceTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useEndorsementLicenceTypeById = (id: string | undefined) =>
  useQuery({
    queryKey: ['endorsementLicenceType', id],
    queryFn: async () => {
      const response = await getApiClient().getEndorsementLicenceTypeById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateEndorsementLicenceType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EndorsementLicenceTypeDTO) => getApiClient().createEndorsementLicenceType(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementLicenceTypes'] }),
  });
};

export const useUpdateEndorsementLicenceType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EndorsementLicenceTypeDTO }) =>
      getApiClient().updateEndorsementLicenceType(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementLicenceTypes'] }),
  });
};

export const useDeleteEndorsementLicenceType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteEndorsementLicenceType(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementLicenceTypes'] }),
  });
};